import React, {FC, ReactNode, useEffect, useMemo, useRef, useState} from 'react';
import { Project, Tag } from '@brg/api';
import styled from "styled-components";
import {NeutralLink, ShowMoreButton} from "@brg/ui";
import { bTag, useModal } from "../../../core";
import { TagsSearchResults } from "./styles.mobile";
import { useSelector, useDispatch } from "react-redux";
import CategoryTagItem from "./CategoryTagitem";
import { BTag, BTagsGrid } from "../../../core/components";
import { useTranslation } from "react-i18next";
import { AddTagField } from "./AddTagField";
import { EmptyText, EmptyTextWrapper } from "./FoundTags"
import { isRequired } from './support/isRequired';
import { CreateTagButton } from './components/CreateTagComponent/style';
import { CreateTagComponent } from './components/CreateTagComponent/CreateTagComponent';
import { TAGS_LEVEL } from './tagsSearchResultsItem';
import {BTagsGridNew} from "../../../core/components/bTag/bTagsGridNew/bTagsGridNew";

export interface SearchResultsProps {
  tags: Tag[];
  grouping: number;
  searchText?: string;
  children?: ReactNode;
  onTagSelect: (tag: Tag) => void;
  project?: Project,
  requiredTags?: string[],
  shouldVisibleEducationCategory?: boolean;
  isMobileMenu?: boolean;
  visible?: boolean;
  viewAllTags?: boolean;
  isEdit?: boolean;
  onMoreClick?: () => void;
  showAllTagsInGroup?: boolean;
}

export const CroppedTagsGrid = styled(BTagsGrid)`
  margin-bottom: 0px !important;
`

export const SearchResultsRoot = styled.div`
  margin-top: 16px;
  padding-bottom: 80px;
`

export const AddButton = styled.div`
  color: #007bff;
  cursor: pointer;
  margin-left: 5px;

  &:hover {
    color: #0056b3;
  }
`

const MemoizedBTag = React.memo(BTag);

export const SearchResults: FC<SearchResultsProps> = ({
  tags = [],
  children,
  visible,
  grouping,
  requiredTags,
  onTagSelect,
  isMobileMenu,
  viewAllTags,
  isEdit,
  onMoreClick,
  showAllTagsInGroup,
}) => {
  const { current: headerTag } = useSelector((state: any) => state.tags.headerTag);
  const searchText = useSelector((state: any) => state.tags.searchText);
  const selectedTagsIndex = useSelector((state: any) => state.tags.selectedTagsIndex);
  const isEditProjectPage = window.location.pathname.startsWith('/edit');
  const container = useRef(null);
  const { t } = useTranslation();
  const [isCreateMode, setIsCreateMode] = useState(false);
  const modal = useModal();
  const dispatch = useDispatch();

  const shouldSortedRequiredTags = !!requiredTags?.length;

  const isTagRelevant = (tag: Tag) => {
    return searchText && (tag?.title?.toLocaleLowerCase().includes((searchText.toLocaleLowerCase())) || tag?.name?.toLocaleLowerCase().includes((searchText.toLocaleLowerCase())));
  }

  const handleShowMoreClick = () => {
    if (isEdit || isEditProjectPage) {
      // В режиме редактирования развертываем список тегов
      onMoreClick && onMoreClick();
    } else {
      // В обычном режиме переходим на страницу категории
      modal.hide();
    }
  }

  const filterInnerTags = (tags: any) => {
    return tags.reduce((result: any, tag: any) => {
      const { additionalTags, level } = tag;
      const isRelevantMainTag = level === 0 || (level === 1 && isTagRelevant(tag));
      return [
        ...result,
        ...additionalTags,
        ...(isRelevantMainTag ? [tag] : [])
      ];
    }, [])
  };

  const filteredTags = useMemo(() => {
    if (!tags.length) return [];

    let result: Tag[] = [];
    if (grouping === 2) {
      result = tags.flatMap(tag => tag.additionalTags ?? []);
    } else if (grouping === 3) {
      result = tags.flatMap(tag => {
        const isRelevantMainTag = [1, 2].includes(tag.level) && isTagRelevant(tag);
        return [...filterInnerTags(tag.additionalTags ?? []), ...(isRelevantMainTag ? [tag] : [])];
      });
    } else result = tags

    if (shouldSortedRequiredTags) {
      result.sort((a, b) => (isRequired(a, requiredTags) ? -1 : 0));
    }

    return result;
  }, [grouping, requiredTags, shouldSortedRequiredTags, tags])

  const getTagsAddConditions = (arr: any) => {
    return arr?.map((tag: Tag) => {
      if (tag.level === TAGS_LEVEL.children && !tag?.additionalTags?.length) {
        return {...tag,level: TAGS_LEVEL.child}
      }
      return tag
    })
  }

  const byLevel: { [key: number]: Tag[] } = useMemo(() => {
    const levels: { [key: number]: Tag[] } = { 0: [], 1: [], 2: [] };

    for (const tag of getTagsAddConditions(filteredTags)) {
      levels[tag.level]?.push(tag);
    }

    return levels;
  }, [filteredTags]);

  const selectedIndexes = useMemo(() => new Set(Object.keys(selectedTagsIndex)), [selectedTagsIndex]);

  useEffect(() => {
    //@ts-ignore
    container?.current?.scrollTo(0,0)
  }, [headerTag?.tagId])

  if (!visible) return null;

  if (searchText && !filteredTags.length && headerTag && headerTag.typeCategory === 'open') {
    return (
      <TagsSearchResults directionMobile="column">
        {children}
        {isEditProjectPage && <AddTagField parentTag={headerTag} name={searchText}/>}
      </TagsSearchResults>
    )
  }

  if (isCreateMode) {
    return (
      <CreateTagComponent 
        onClose={() => setIsCreateMode(false)} 
      />
    )
  }

  if (searchText && !filteredTags.length && !isCreateMode) {
    return (
      <EmptyTextWrapper directionMobile='column' gy={viewAllTags ? 24 : 0}  width="100vw" align="center" justify="center" isMobileMenu={isMobileMenu}>
        <EmptyText>{t("tags_modal.empty_search_select_from_list")}</EmptyText>
        {viewAllTags && <CreateTagButton onClick={() => setIsCreateMode(true)}>{t("tags_modal.create_tag")}</CreateTagButton>}
      </EmptyTextWrapper>
    )
  }
  
  return (
    <TagsSearchResults ref={container} directionMobile="column">
      {children}
      <SearchResultsRoot className={'tags-search-result-root'}>
        {!!byLevel[0].length && (
          <BTagsGridNew style={{paddingBottom: 8}} noLimitMaxHeight>
            {byLevel[0].map((item) => (
              <MemoizedBTag
                key={item.tagId}
                tag={item}
                onClick={() => onTagSelect(item)}
                selected={selectedIndexes.has(item.tagId)}
                educational={isRequired(item, requiredTags)}
                organization={item?.isTeamTag}
              >
                {bTag.title(item)}
              </MemoizedBTag>
            ))}
            {headerTag && headerTag?.level === 2 && headerTag?.isShowAll && searchText.length === 0 && !showAllTagsInGroup && (
              isEdit || isEditProjectPage ? (
                <ShowMoreButton
                    onClick={handleShowMoreClick}
                >{t("tags_button_more")}</ShowMoreButton>
              ) : (
                <NeutralLink to={`/tag/category/${headerTag?.tagId}`}
                            style={{ display: 'inline-flex', width: 'max-content' }}
                            onClick={() => modal.hide()}
                >
                  <ShowMoreButton
                      external
                  >{t("tags_button_more")}</ShowMoreButton>
                </NeutralLink>
              )
            )}
          </BTagsGridNew>
        )}
        {byLevel[1].map((item) => (
          <CategoryTagItem
            tag={item}
            key={item.tagId}
            onSelect={onTagSelect}
            selected={selectedTagsIndex[item.tagId]}
          />
        ))}
        {byLevel[2].map((item) => (
          <CategoryTagItem
            tag={item}
            key={item.tagId}
            onSelect={onTagSelect}
            selected={selectedTagsIndex[item.tagId]}
          />
        ))}
      </SearchResultsRoot>
    </TagsSearchResults>
  );
};
import React, {FC, ReactNode, useEffect, useMemo, useRef, useState} from 'react';
import { Project, Tag } from '@brg/api';
import styled from "styled-components";
import {NeutralLink, ShowMoreButton} from "@brg/ui";
import { bTag, useModal } from "../../../core";
import { TagsSearchResults } from "./styles.mobile";
import { useSelector, useDispatch } from "react-redux";
import CategoryTagItem from "./CategoryTagitem";
import { BTag, BTagsGrid } from "../../../core/components";
import { useTranslation } from "react-i18next";
import { AddTagField } from "./AddTagField";
import { EmptyText, EmptyTextWrapper } from "./FoundTags"
import { isRequired } from './support/isRequired';
import { CreateTagButton } from './components/CreateTagComponent/style';
import { CreateTagComponent } from './components/CreateTagComponent/CreateTagComponent';
import { TAGS_LEVEL } from './tagsSearchResultsItem';
import {BTagsGridNew} from "../../../core/components/bTag/bTagsGridNew/bTagsGridNew";

export interface SearchResultsProps {
  tags: Tag[];
  grouping: number;
  searchText?: string;
  children?: ReactNode;
  onTagSelect: (tag: Tag) => void;
  project?: Project,
  requiredTags?: string[],
  shouldVisibleEducationCategory?: boolean;
  isMobileMenu?: boolean;
  visible?: boolean;
  viewAllTags?: boolean;
  isEdit?: boolean;
  onMoreClick?: () => void;
  showAllTagsInGroup?: boolean;
}

export const CroppedTagsGrid = styled(BTagsGrid)`
  margin-bottom: 0px !important;
`

export const SearchResultsRoot = styled.div`
  margin-top: 16px;
  padding-bottom: 80px;
`

export const AddButton = styled.div`
  color: #007bff;
  cursor: pointer;
  margin-left: 5px;

  &:hover {
    color: #0056b3;
  }
`

const MemoizedBTag = React.memo(BTag);

export const SearchResults: FC<SearchResultsProps> = ({
  tags = [],
  children,
  visible,
  grouping,
  requiredTags,
  onTagSelect,
  isMobileMenu,
  viewAllTags,
  isEdit,
  onMoreClick,
  showAllTagsInGroup,
}) => {
  const { current: headerTag } = useSelector((state: any) => state.tags.headerTag);
  const searchText = useSelector((state: any) => state.tags.searchText);
  const selectedTagsIndex = useSelector((state: any) => state.tags.selectedTagsIndex);
  const isEditProjectPage = window.location.pathname.startsWith('/edit');
  const container = useRef(null);
  const { t } = useTranslation();
  const [isCreateMode, setIsCreateMode] = useState(false);
  const modal = useModal();
  const dispatch = useDispatch();

  const shouldSortedRequiredTags = !!requiredTags?.length;

  const isTagRelevant = (tag: Tag) => {
    return searchText && (tag?.title?.toLocaleLowerCase().includes((searchText.toLocaleLowerCase())) || tag?.name?.toLocaleLowerCase().includes((searchText.toLocaleLowerCase())));
  }

  const handleShowMoreClick = () => {
    if (isEdit || isEditProjectPage) {
      // В режиме редактирования развертываем список тегов
      onMoreClick && onMoreClick();
    } else {
      // В обычном режиме переходим на страницу категории
      modal.hide();
    }
  }

  const filterInnerTags = (tags: any) => {
    return tags.reduce((result: any, tag: any) => {
      const { additionalTags, level } = tag;
      const isRelevantMainTag = level === 0 || (level === 1 && isTagRelevant(tag));
      return [
        ...result,
        ...additionalTags,
        ...(isRelevantMainTag ? [tag] : [])
      ];
    }, [])
  };

  const filteredTags = useMemo(() => {
    if (!tags.length) return [];

    let result: Tag[] = [];
    if (grouping === 2) {
      result = tags.flatMap(tag => tag.additionalTags ?? []);
    } else if (grouping === 3) {
      result = tags.flatMap(tag => {
        const isRelevantMainTag = [1, 2].includes(tag.level) && isTagRelevant(tag);
        return [...filterInnerTags(tag.additionalTags ?? []), ...(isRelevantMainTag ? [tag] : [])];
      });
    } else result = tags

    if (shouldSortedRequiredTags) {
      result.sort((a, b) => (isRequired(a, requiredTags) ? -1 : 0));
    }

    return result;
  }, [grouping, requiredTags, shouldSortedRequiredTags, tags])

  const getTagsAddConditions = (arr: any) => {
    return arr?.map((tag: Tag) => {
      if (tag.level === TAGS_LEVEL.children && !tag?.additionalTags?.length) {
        return {...tag,level: TAGS_LEVEL.child}
      }
      return tag
    })
  }

  const byLevel: { [key: number]: Tag[] } = useMemo(() => {
    const levels: { [key: number]: Tag[] } = { 0: [], 1: [], 2: [] };

    for (const tag of getTagsAddConditions(filteredTags)) {
      levels[tag.level]?.push(tag);
    }

    return levels;
  }, [filteredTags]);

  const selectedIndexes = useMemo(() => new Set(Object.keys(selectedTagsIndex)), [selectedTagsIndex]);

  useEffect(() => {
    //@ts-ignore
    container?.current?.scrollTo(0,0)
  }, [headerTag?.tagId])

  if (!visible) return null;

  if (searchText && !filteredTags.length && headerTag && headerTag.typeCategory === 'open') {
    return (
      <TagsSearchResults directionMobile="column">
        {children}
        {isEditProjectPage && <AddTagField parentTag={headerTag} name={searchText}/>}
      </TagsSearchResults>
    )
  }

  if (isCreateMode) {
    return (
      <CreateTagComponent 
        onClose={() => setIsCreateMode(false)} 
      />
    )
  }

  if (searchText && !filteredTags.length && !isCreateMode) {
    return (
      <EmptyTextWrapper directionMobile='column' gy={viewAllTags ? 24 : 0}  width="100vw" align="center" justify="center" isMobileMenu={isMobileMenu}>
        <EmptyText>{t("tags_modal.empty_search_select_from_list")}</EmptyText>
        {viewAllTags && <CreateTagButton onClick={() => setIsCreateMode(true)}>{t("tags_modal.create_tag")}</CreateTagButton>}
      </EmptyTextWrapper>
    )
  }
  
  return (
    <TagsSearchResults ref={container} directionMobile="column">
      {children}
      <SearchResultsRoot className={'tags-search-result-root'}>
        {!!byLevel[0].length && (
          <BTagsGridNew style={{paddingBottom: 8}} noLimitMaxHeight>
            {byLevel[0].map((item) => (
              <MemoizedBTag
                key={item.tagId}
                tag={item}
                onClick={() => onTagSelect(item)}
                selected={selectedIndexes.has(item.tagId)}
                educational={isRequired(item, requiredTags)}
                organization={item?.isTeamTag}
              >
                {bTag.title(item)}
              </MemoizedBTag>
            ))}
            {headerTag && headerTag?.level === 2 && headerTag?.isShowAll && searchText.length === 0 && !showAllTagsInGroup && (
              isEdit || isEditProjectPage ? (
                <ShowMoreButton
                    onClick={handleShowMoreClick}
                >{t("tags_button_more")}</ShowMoreButton>
              ) : (
                <NeutralLink to={`/tag/category/${headerTag?.tagId}`}
                            style={{ display: 'inline-flex', width: 'max-content' }}
                            onClick={() => modal.hide()}
                >
                  <ShowMoreButton
                      external
                  >{t("tags_button_more")}</ShowMoreButton>
                </NeutralLink>
              )
            )}
          </BTagsGridNew>
        )}
        {byLevel[1].map((item) => (
          <CategoryTagItem
            tag={item}
            key={item.tagId}
            onSelect={onTagSelect}
            selected={selectedTagsIndex[item.tagId]}
          />
        ))}
        {byLevel[2].map((item) => (
          <CategoryTagItem
            tag={item}
            key={item.tagId}
            onSelect={onTagSelect}
            selected={selectedTagsIndex[item.tagId]}
          />
        ))}
      </SearchResultsRoot>
    </TagsSearchResults>
  );
};
